
import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Tooltip, Box,
  Divider, Autocomplete, IconButton
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Assignment as AnamneseIcon,
  DeleteOutline as DeleteIcon,
  AddCircle as AddCircleIcon,
  ReceiptLong as ReceiptLongIcon,
  Cancel as CancelIcon,
  Replay as ReplayIcon,
  CheckCircle as CheckCircleIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import { useTheme as useAppTheme } from '../context/ThemeContext';
import api from '../services/api';

function formatarReaisInput(valor) {
  if (!valor) return 'R$ 0,00';
  valor = String(valor).replace(/[^\d]/g, '');
  let intValue = parseInt(valor, 10);
  if (isNaN(intValue)) intValue = 0;
  let str = (intValue / 100).toFixed(2);
  let [inteiro, decimal] = str.split('.');
  inteiro = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `R$ ${inteiro},${decimal}`;
}

function parseReais(str) {
  if (!str) return 0;
  return Number(String(str).replace(/[^\d]/g, '')) / 100;
}

const STATUS_OPTIONS = [
  { value: 'fechado',     label: 'Fechado',            color: '#22c55e', bg: 'rgba(34,197,94,0.1)' },
  { value: 'nao_fechado', label: 'Não Fechou',          color: '#ef4444', bg: 'rgba(239,68,68,0.1)' },
  { value: 'retornar',    label: 'Ficou de Retornar',   color: '#F97316', bg: 'rgba(249,115,22,0.1)' },
];

const ModalOrcamento = ({ open, onClose, procedimentos, onSave, pacienteTelefone, pacienteNome, orcamentoData, somenteLeitura, onEnviarAnamnese, onDelete }) => {
  const { isDarkMode } = useAppTheme();

  const [procedimentosSelecionados, setProcedimentosSelecionados] = useState([]);
  const [valores, setValores] = useState({});
  const [observacoes, setObservacoes] = useState('');
  const [status, setStatus] = useState('pendente');
  const [gerandoPdf, setGerandoPdf] = useState(false);

  useEffect(() => {
    if (orcamentoData) {
      setProcedimentosSelecionados(orcamentoData.procedimentos || []);
      // O campo de valor trabalha com dígitos-como-centavos (máscara de
      // digitação), mas o banco guarda o valor real em reais — sem essa
      // conversão, R$ 300,00 salvo aparecia como R$ 3,00 ao reabrir.
      const valoresConvertidos = {};
      Object.entries(orcamentoData.valores || {}).forEach(([id, v]) => {
        valoresConvertidos[id] = String(Math.round(parseFloat(v || 0) * 100));
      });
      setValores(valoresConvertidos);
      setObservacoes(orcamentoData.observacoes || '');
      setStatus(orcamentoData.status || 'pendente');
    } else {
      setProcedimentosSelecionados([]);
      setValores({});
      setObservacoes('');
      setStatus('pendente');
    }
  }, [orcamentoData, open]);

  const procedimentosArray = useMemo(
    () => Array.isArray(procedimentosSelecionados) ? procedimentosSelecionados : [],
    [procedimentosSelecionados]
  );

  // Só oferece na busca quem ainda não foi adicionado ao orçamento
  const procedimentosDisponiveis = useMemo(
    () => procedimentos.filter(p => !procedimentosArray.includes(p.id)),
    [procedimentos, procedimentosArray]
  );

  const itensSelecionados = useMemo(
    () => procedimentosArray
      .map(id => procedimentos.find(p => p.id === id))
      .filter(Boolean),
    [procedimentosArray, procedimentos]
  );

  const handleAdicionarProcedimento = (procedimento) => {
    if (!procedimento || somenteLeitura) return;
    setProcedimentosSelecionados(prev => prev.includes(procedimento.id) ? prev : [...prev, procedimento.id]);
    // Puxa o valor cadastrado do procedimento como ponto de partida — o
    // dentista pode editar depois, mas evita ter que digitar tudo do zero.
    const valorPadrao = parseFloat(procedimento.valorPadrao || 0);
    if (valorPadrao > 0) {
      setValores(prev => (prev[procedimento.id] ? prev : { ...prev, [procedimento.id]: String(Math.round(valorPadrao * 100)) }));
    }
  };

  const handleRemoverProcedimento = (id) => {
    if (somenteLeitura) return;
    setProcedimentosSelecionados(prev => prev.filter(p => p !== id));
    setValores(prev => {
      const { [id]: _removido, ...resto } = prev;
      return resto;
    });
  };

  const handleValorChange = (id, e) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    setValores(prev => ({ ...prev, [id]: value }));
  };

  const valorTotal = procedimentosArray.reduce((acc, id) => acc + parseReais(valores[id]), 0);

  const handleSave = () => {
    const valoresFloat = {};
    Object.keys(valores).forEach(id => { valoresFloat[id] = parseReais(valores[id]); });
    onSave({ procedimentos: procedimentosSelecionados, valores: valoresFloat, observacoes, status });
    onClose();
  };

  function montarMensagemWhatsapp() {
    let msg = `Olá${pacienteNome ? ' ' + pacienteNome : ''}! Segue seu orçamento:\n`;
    itensSelecionados.forEach(p => {
      msg += `- ${p.nome}: ${formatarReaisInput(valores[p.id] || '')}\n`;
    });
    msg += `Valor total: ${formatarReaisInput(String(Math.round(valorTotal * 100)))}\n`;
    if (observacoes) msg += `Obs: ${observacoes}\n`;
    msg += 'Qualquer dúvida, estamos à disposição!';
    return msg;
  }

  function enviarWhatsapp() {
    if (!pacienteTelefone) { alert('Telefone do paciente não informado!'); return; }
    const numero = pacienteTelefone.replace(/\D/g, '');
    window.open(`https://wa.me/55${numero}?text=${encodeURIComponent(montarMensagemWhatsapp())}`, '_blank');
  }

  // Gera o PDF (plano de tratamento com os dados da clínica) e baixa —
  // depois abre o WhatsApp do paciente já com uma mensagem pronta, pra só
  // anexar o arquivo que acabou de baixar (o WhatsApp normal não permite
  // anexar arquivo automaticamente por link, só a API paga da Meta faz isso).
  async function gerarEEnviarPdf() {
    if (!orcamentoData?.id) return;
    setGerandoPdf(true);
    try {
      const blob = await api.get(`/orcamentos/${orcamentoData.id}/pdf`, { responseType: 'blob' });
      const nomeArquivo = `orcamento-${(pacienteNome || 'paciente').replace(/\s+/g, '-')}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nomeArquivo;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      if (pacienteTelefone) {
        const numero = pacienteTelefone.replace(/\D/g, '');
        const msg = `Olá${pacienteNome ? ' ' + pacienteNome : ''}! Segue seu orçamento em PDF`;
        window.open(`https://wa.me/55${numero}?text=${encodeURIComponent(msg)}`, '_blank');
      }
    } catch {
      alert('Não foi possível gerar o PDF do orçamento.');
    }
    setGerandoPdf(false);
  }

  // Destaque visual conforme o status do orçamento já salvo — deixa claro de
  // cara, sem precisar ler, que esse orçamento não foi fechado ou ainda
  // depende de um retorno.
  const DESTAQUE_STATUS = {
    fechado: { cor: '#22c55e', icon: CheckCircleIcon, label: 'Fechado' },
    nao_fechado: { cor: '#ef4444', icon: CancelIcon, label: 'Não Fechou' },
    retornar: { cor: '#f59e0b', icon: ReplayIcon, label: 'Ficou de Retornar' },
  };
  const destaque = DESTAQUE_STATUS[orcamentoData?.status];

  const paperSx = isDarkMode
    ? { background: 'linear-gradient(180deg, rgba(30,41,59,0.98) 0%, rgba(24,33,48,0.98) 100%)', border: `1px solid ${destaque ? `${destaque.cor}59` : 'rgba(249,115,22,0.2)'}`, borderTop: destaque ? `4px solid ${destaque.cor}` : undefined, borderRadius: '20px', boxShadow: destaque ? `0 24px 60px ${destaque.cor}40` : '0 24px 60px rgba(0,0,0,0.5)' }
    : { background: '#FFFDFB', border: `1px solid ${destaque ? `${destaque.cor}40` : 'rgba(249,115,22,0.12)'}`, borderTop: destaque ? `4px solid ${destaque.cor}` : undefined, borderRadius: '20px', boxShadow: destaque ? `0 24px 60px ${destaque.cor}2e` : '0 24px 60px rgba(249,115,22,0.15)' };

  const inputRadiusSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '&.Mui-focused fieldset': { borderColor: '#F97316' },
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: paperSx }}>

      {/* Header */}
      <DialogTitle sx={{ pb: 2, pt: 2.75, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: '12px', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: destaque ? `linear-gradient(135deg, ${destaque.cor} 0%, ${destaque.cor} 100%)` : 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
              boxShadow: destaque ? `0 4px 12px ${destaque.cor}66` : '0 4px 12px rgba(249,115,22,0.35)',
            }}>
              {destaque ? <destaque.icon sx={{ color: '#fff', fontSize: '1.3rem' }} /> : <ReceiptLongIcon sx={{ color: '#fff', fontSize: '1.3rem' }} />}
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
                  {somenteLeitura ? 'Orçamento' : 'Criar Orçamento'}
                </Typography>
                {destaque && (
                  <Box sx={{ px: 1, py: 0.15, borderRadius: '6px', bgcolor: `${destaque.cor}1f`, border: `1px solid ${destaque.cor}4d` }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: destaque.cor, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {destaque.label}
                    </Typography>
                  </Box>
                )}
              </Box>
              {pacienteNome && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {pacienteNome}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary', '&:hover': { color: '#F97316', bgcolor: 'rgba(249,115,22,0.08)' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider sx={{ borderColor: 'rgba(249,115,22,0.12)' }} />

      <DialogContent sx={{ px: 3, py: 2.5 }}>

        {/* Adicionar procedimento */}
        {!somenteLeitura && (
          <Autocomplete
            options={procedimentosDisponiveis}
            getOptionLabel={(p) => p.nome || ''}
            value={null}
            onChange={(e, novo) => handleAdicionarProcedimento(novo)}
            noOptionsText="Nenhum procedimento encontrado"
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Buscar e adicionar procedimento..."
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <AddCircleIcon sx={{ mr: 1, color: '#F97316', fontSize: '1.15rem' }} />,
                }}
              />
            )}
            sx={{ mb: 2.5, ...inputRadiusSx }}
          />
        )}

        {/* Lista de procedimentos selecionados */}
        <Typography variant="caption" sx={{ color: '#F97316', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
          Procedimentos do orçamento {itensSelecionados.length > 0 && `(${itensSelecionados.length})`}
        </Typography>

        {itensSelecionados.length === 0 ? (
          <Box sx={{
            p: 3, borderRadius: '14px', textAlign: 'center', mb: 2.5,
            border: '1.5px dashed rgba(249,115,22,0.25)',
            bgcolor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(249,115,22,0.03)',
          }}>
            <Typography variant="body2" color="text.secondary">
              {somenteLeitura ? 'Nenhum procedimento neste orçamento.' : 'Busque acima e adicione os procedimentos do orçamento.'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.5 }}>
            {itensSelecionados.map(p => (
              <Box
                key={p.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  p: '10px 8px 10px 14px',
                  borderRadius: '12px',
                  border: '1px solid rgba(249,115,22,0.15)',
                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : '#fff',
                }}
              >
                <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {p.nome}
                </Typography>
                <TextField
                  size="small"
                  value={formatarReaisInput(valores[p.id] || '')}
                  onChange={e => handleValorChange(p.id, e)}
                  disabled={somenteLeitura}
                  inputProps={{ inputMode: 'numeric', maxLength: 15, style: { textAlign: 'right' } }}
                  sx={{ width: '128px', flexShrink: 0, ...inputRadiusSx }}
                />
                {!somenteLeitura && (
                  <IconButton size="small" onClick={() => handleRemoverProcedimento(p.id)} sx={{ color: 'text.disabled', '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.08)' } }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Total */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          p: '14px 18px', borderRadius: '14px',
          background: 'linear-gradient(135deg, rgba(249,115,22,0.12) 0%, rgba(249,115,22,0.05) 100%)',
          border: '1px solid rgba(249,115,22,0.2)',
          mb: 2.5
        }}>
          <Typography variant="body2" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.75rem' }}>
            Valor Total
          </Typography>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#F97316' }}>
            {formatarReaisInput(String(Math.round(valorTotal * 100)))}
          </Typography>
        </Box>

        {/* Observações */}
        <TextField
          placeholder="Observações (opcional)..."
          value={observacoes}
          onChange={e => setObservacoes(e.target.value)}
          fullWidth
          multiline
          rows={2}
          size="small"
          disabled={somenteLeitura}
          sx={{ mb: 2.5, ...inputRadiusSx }}
        />

        {/* Status */}
        <Typography variant="caption" sx={{ color: '#F97316', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
          Status do Orçamento *
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {STATUS_OPTIONS.map(opt => {
            const ativo = status === opt.value;
            return (
              <Box
                key={opt.value}
                onClick={() => !somenteLeitura && setStatus(opt.value)}
                sx={{
                  px: 2, py: 0.75, borderRadius: '20px', cursor: somenteLeitura ? 'default' : 'pointer',
                  border: `1.5px solid ${ativo ? opt.color : 'rgba(0,0,0,0.12)'}`,
                  bgcolor: ativo ? opt.bg : 'transparent',
                  color: ativo ? opt.color : 'text.secondary',
                  fontWeight: ativo ? 700 : 400,
                  fontSize: '0.8rem',
                  transition: 'all 0.15s',
                  userSelect: 'none',
                  '&:hover': somenteLeitura ? {} : { borderColor: opt.color, bgcolor: opt.bg, color: opt.color }
                }}
              >
                {opt.label}
              </Box>
            );
          })}
        </Box>
        {!somenteLeitura && status === 'pendente' && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }}>
            Selecione um status pra poder salvar.
          </Typography>
        )}

      </DialogContent>

      <Divider sx={{ borderColor: 'rgba(249,115,22,0.12)' }} />

      {/* Actions */}
      <DialogActions sx={{ px: 2.5, py: 1.75, gap: 0.5 }}>
        {onDelete && (
          <Tooltip title="Excluir orçamento">
            <IconButton
              onClick={() => {
                if (window.confirm('Deseja excluir este orçamento?')) {
                  onDelete(orcamentoData?.id || null);
                }
              }}
              size="small"
              sx={{ color: 'text.disabled', '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.08)' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onEnviarAnamnese && (
          <Tooltip title="Abrir formulário de anamnese do paciente">
            <IconButton
              onClick={onEnviarAnamnese}
              size="small"
              sx={{ color: 'text.disabled', '&:hover': { color: '#6366F1', bgcolor: 'rgba(99,102,241,0.08)' } }}
            >
              <AnamneseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={pacienteTelefone ? 'Enviar orçamento no WhatsApp' : 'Cadastre o telefone do paciente'}>
          <span>
            <IconButton
              onClick={enviarWhatsapp}
              disabled={!pacienteTelefone}
              size="small"
              sx={{ color: 'text.disabled', '&:hover': { color: '#25D366', bgcolor: 'rgba(37,211,102,0.08)' } }}
            >
              <WhatsAppIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {orcamentoData?.id && (
          <Tooltip title="Gerar PDF do orçamento (com dados da clínica) e enviar no WhatsApp">
            <span>
              <IconButton
                onClick={gerarEEnviarPdf}
                disabled={gerandoPdf}
                size="small"
                sx={{ color: 'text.disabled', '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.08)' } }}
              >
                <PictureAsPdfIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Box sx={{ flex: 1 }} />

        <Button
          onClick={onClose}
          sx={{ color: 'text.secondary', fontWeight: 600, borderRadius: '10px' }}
        >
          Fechar
        </Button>
        {(!somenteLeitura || status === 'retornar' || status === 'pendente') && (
          <Button
            onClick={handleSave}
            variant="contained"
            disableElevation
            disabled={status === 'pendente'}
            startIcon={<SaveIcon />}
            sx={{
              bgcolor: '#F97316',
              '&:hover': { bgcolor: '#EA580C' },
              '&.Mui-disabled': { bgcolor: 'rgba(249,115,22,0.25)', color: 'rgba(0,0,0,0.4)' },
              borderRadius: '10px', fontWeight: 700, px: 2.5,
            }}
          >
            Salvar
          </Button>
        )}
      </DialogActions>

    </Dialog>
  );
};

export default ModalOrcamento;
